import React, { useEffect, useMemo } from "react";

import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { LoginResponseUser } from "@api-types/user/LoginResponseDto";
import CookieConsent from "@components-shared/CookieConsent";
import { CapacitorAppDisabledPopup } from "@components/block-update";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { ErrorBoundary } from "@lib/error-boundary";
import { history } from "@state";
import { RootAppStoreContext } from "@state/context/root-store/RootAppStoreContext";
import VersionChangeDetector from "@state/utils/VersionChangeDetector";
import { getCurrentCulture } from "@utils";
import { analyticsTracker } from "@utils/analytics";
import { useCapacitorPlatform } from "@utils/specific/capacitor";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import useSafeAreaListener from "@utils/specific/capacitor/useSafeAreaListener";
import { StorageStateKeysEnum } from "@utils/storage";
import { observer } from "mobx-react";
// @ts-ignore
import MainLayout from "themes/layouts/MainLayout";

export default observer(function AppRoot() {
	const location = useLocation();

	const versionChangeDetector = useMemo(
		() =>
			// Interval is 10 min
			new VersionChangeDetector(() => {
				if (
					!App.state.history.location.pathname.includes("/app-update")
				) {
					App.state.redirect(`/${getCurrentCulture()}/app-update`);
				}
			}, 600000),
		[]
	);

	useEffect(() => {
		console.log("....", WEBPACK_GIT_COMMIT_HASH);
		// show only on dev, qa, tl
		if (WEBPACK_BASE_ADDRESS.indexOf("develop") != -1) {
			console.log("....", WEBPACK_GIT_BRANCH);
			console.log("....", WEBPACK_GIT_LAST_COMMIT_DATE);
		}

		let setTimeoutCB: ReturnType<typeof setTimeout> | null = null;

		function enableNetConnection() {
			if (setTimeoutCB != null) {
				clearTimeout(setTimeoutCB);
				setTimeoutCB = null;
			}
		}

		function disableNetConnection() {
			setTimeoutCB = setTimeout(() => {
				if (!window.navigator.onLine) {
					history.push(
						`/${getCurrentCulture()}/offline?lastVisitedUrl=${
							location.pathname
						}`
					);
					setTimeoutCB = null;
				}
			}, 5000);
		}

		if (isCapacitorPlatform()) {
			App.state.rootStore.capacitorConnectionStatusStore.onInitialize();
		} else {
			window.addEventListener("offline", disableNetConnection);
			window.addEventListener("online", enableNetConnection);
		}

		return () => {
			if (isCapacitorPlatform()) {
				App.state.rootStore.capacitorConnectionStatusStore.onDispose();
			} else {
				window.removeEventListener("offline", disableNetConnection);
				window.removeEventListener("online", enableNetConnection);
			}
		};
	}, []);

	useEffect(() => {
		App.state.rootStore.onInitialize();

		return () => {
			App.state.rootStore.onDispose();
		};
	}, []);

	useEffect(() => {
		const user = JSON.parse(
			App.state.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.USER_KEY
			) as string
		) as LoginResponseUser | null;

		if (user && analyticsTracker.userData === null) {
			analyticsTracker.trackIsUserLogged(user);
		}
	}, []);

	useEffect(() => {
		if (App.state.history.location.pathname.includes("/app-update")) {
			App.state.history.goBack();
		}

		versionChangeDetector.onInitialize();

		return () => {
			versionChangeDetector.onDispose();
		};
	}, []);

	useEffect(() => {
		setTimeout(() => {
			analyticsTracker.trackPageView(location.pathname);
		}, 200);
	}, [location.pathname]);

	//listener for capacitor events
	useCapacitorPlatform();

	const { bottom } = useSafeAreaListener();

	if (!App.state.rootStore.isStoreInitialized) {
		return null;
	}

	return (
		<RootAppStoreContext.Provider value={App.state.rootStore}>
			<ErrorBoundary>
				<ErrorBoundaryWithPropagation componentName="App.tsx/ToastContainer">
					<ToastContainer
						containerId={"toast-container"}
						position="bottom-right"
						style={bottom > 0 ? { bottom: bottom + 64 } : undefined}
					/>
				</ErrorBoundaryWithPropagation>
			</ErrorBoundary>
			<MainLayout />

			<CapacitorAppDisabledPopup />

			{!isCapacitorPlatform() && (
				<ErrorBoundary>
					<ErrorBoundaryWithPropagation componentName="App.tsx/CookieConsent">
						<CookieConsent />
					</ErrorBoundaryWithPropagation>
				</ErrorBoundary>
			)}
		</RootAppStoreContext.Provider>
	);
});
